/**
 * A4 -- scoring an admission candidate by hand.
 *
 * With `MARSAD_AI_PROVIDER=none` there was no way to assess a
 * candidate at all: Derive answered 503 with an environment variable on
 * screen, which is an instruction for somebody with shell access shown
 * to somebody who has none, and no second route existed. A candidate
 * could be raised and never scored.
 *
 * The methodology's own division of labour is "scores are proposed,
 * rules decide", and nothing in the rules half needs an Engine. So this
 * proposes the same sheet from a person; the decision, the pathway, the
 * tier and the triggers are still derived server-side.
 *
 * The criteria asked for come from the candidate's `pathway`, which the
 * API derives -- the two sets are different, and guessing from the
 * sovereign flag alone would ask for the wrong one on exactly the
 * candidates the routing heuristic exists to catch.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "shared/toast/ToastProvider";
import {
  useAdmissionCriteria,
  useScoreAdmissionManually,
} from "twin/api/ibs_admission_hooks";

/** The pre-P416 sets, used only until the rubric has loaded so the form
 *  never renders an empty sheet. Once `/criteria` answers, the active
 *  regime's rubric names the gates and criteria -- the rules layer
 *  refuses a sheet missing any of them, and says which. */
const FALLBACK: Record<string, { gates: string[]; scored: string[] }> = {
  ces: {
    gates: ["q1_external_customer", "q2_client_impact"],
    scored: ["severity", "breadth", "time_to_impact", "systemic_risk", "firm_safety"],
  },
  ids: {
    gates: ["c1_regulatory_instrument", "c2_kingdom_specific", "c3_transactional"],
    scored: ["time_to_breach", "penalty_severity", "reversibility"],
  },
};

export function ManualAdmissionScores({
  ibsId,
  pathway,
  pathwayReason,
}: {
  ibsId: string;
  pathway: string;
  /** Round 20 W10 -- why this candidate is on this pathway. */
  pathwayReason?: string | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const score = useScoreAdmissionManually();
  // Round 20 W10 -- the scale the rules accept (1 to 5) and each score's
  // band beside it. The form offered a bare 0 to 5 and showed a band only
  // after saving, and a first score on a misread scale excluded SARIE.
  const criteria = useAdmissionCriteria();
  // P416 -- the gates and criteria are the active regime's rubric.
  const lane = (criteria.data?.pathways ?? []).find((p) => p.code === pathway);
  const set = lane
    ? {
        gates: lane.gates.map((g) => g.key),
        scored: lane.criteria.map((c) => c.key),
      }
    : (FALLBACK[pathway] ?? FALLBACK.ces!);
  const bandsOf = (k: string): string[] =>
    [
      ...(lane?.criteria ?? []),
      ...(criteria.data?.ces ?? []),
      ...(criteria.data?.ids ?? []),
    ].find((c) => c.key === k)?.bands ?? [];
  const pathwayKey = ["ces", "ids", "critical_operation"].includes(pathway)
    ? pathway
    : "ces";
  const lo = criteria.data?.min_score ?? 1;
  const hi = criteria.data?.max_score ?? 5;
  const range = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  const reasonKey = `modules.twin.ibsAdmission.manual.pathwayReason.${pathwayReason ?? "customer"}`;

  const [gates, setGates] = useState<Record<string, boolean>>({});
  const [scores, setScores] = useState<Record<string, number>>({});
  const [rationale, setRationale] = useState("");

  // Every criterion, answered. A blank left to default to zero would
  // put a verdict on a question nobody was asked -- and the API
  // refuses it anyway, so allowing submit would only move the refusal.
  const complete = set.scored.every((k) => scores[k] !== undefined);

  return (
    <div data-testid="ibs-manual-scores" className="space-y-2">
      <p
        data-testid="ibs-manual-pathway"
        className="text-xs font-medium text-slate-800"
      >
        {t(`modules.twin.ibsAdmission.manual.pathway.${pathwayKey}`, {
          count: set.scored.length,
        })}{" "}
        <span className="font-normal text-slate-600">{t(reasonKey)}</span>
      </p>
      <p className="text-xs text-slate-600">
        {t("modules.twin.ibsAdmission.manual.explainer", { lo, hi })}
      </p>
      <div className="flex flex-wrap gap-3">
        {set.gates.map((k) => (
          <label key={k} className="flex items-center gap-1.5 text-xs text-slate-700">
            <input
              type="checkbox"
              data-testid={`ibs-manual-gate-${k}`}
              checked={gates[k] ?? false}
              onChange={(e) => setGates({ ...gates, [k]: e.target.checked })}
            />
            {t(`modules.twin.ibsAdmission.criterion.${k}`)}
          </label>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {set.scored.map((k) => (
          <label
            key={k}
            className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-xs"
          >
            <span className="text-slate-700">
              {t(`modules.twin.ibsAdmission.criterion.${k}`)}
            </span>
            <select
              data-testid={`ibs-manual-score-${k}`}
              value={scores[k] ?? ""}
              onChange={(e) => setScores({ ...scores, [k]: Number(e.target.value) })}
              className="rounded border border-slate-300 px-2 py-1"
            >
              <option value="">{t("modules.twin.ibsAdmission.manual.unscored")}</option>
              {range.map((n) => {
                const band = bandsOf(k)[n - lo];
                return (
                  <option key={n} value={n}>
                    {band ? `${n} · ${band}` : n}
                  </option>
                );
              })}
            </select>
            {scores[k] !== undefined && bandsOf(k)[scores[k]! - lo] && (
              <span
                data-testid={`ibs-manual-band-${k}`}
                className="basis-full text-[11px] text-slate-500"
              >
                {bandsOf(k)[scores[k]! - lo]}
              </span>
            )}
          </label>
        ))}
      </div>
      <input
        dir="auto"
        data-testid="ibs-manual-rationale"
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        placeholder={t("modules.twin.ibsAdmission.manual.why")}
        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
      />
      <button
        type="button"
        data-testid="ibs-manual-submit"
        disabled={!complete || score.isPending}
        onClick={() =>
          score.mutate(
            {
              ibsId,
              scores: { ...gates, ...scores },
              ...(rationale.trim() ? { rationale: rationale.trim() } : {}),
            },
            {
              onSuccess: () =>
                toast.push({
                  tone: "success",
                  message: t("modules.twin.ibsAdmission.manual.scored"),
                }),
              // The server names the criterion it is missing; that is
              // more use than a generic failure.
              onError: (e) =>
                toast.push({
                  tone: "error",
                  message: e.detail || t("modules.twin.ibsAdmission.manual.failed"),
                }),
            },
          )
        }
        className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
      >
        {score.isPending
          ? t("modules.twin.ibsAdmission.manual.scoring")
          : t("modules.twin.ibsAdmission.manual.submit")}
      </button>
    </div>
  );
}
